
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslations } from '@/i18n/use-translations';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useAuth } from '@/contexts/auth-context';
import { api } from '@/lib/api';
import { EntityDefinition } from '@fyso/shared';
import {
  LayoutDashboard,
  Database,
  GitBranch,
  Globe,
  Code2,
  Settings,
  Plus,
  Wrench,
  Key,
  FileText,
  ScrollText,
  Activity,
} from 'lucide-react';

const NAVIGATION_ITEMS = [
  { labelKey: 'dashboard', href: '/dashboard', icon: LayoutDashboard },
  { labelKey: 'activity', href: '/activity', icon: Activity },
  { labelKey: 'entities', href: '/settings/entities', icon: Database },
  { labelKey: 'businessRules', href: '/settings/rules', icon: GitBranch },
  { labelKey: 'sites', href: '/sites', icon: Globe },
  { labelKey: 'apiExplorer', href: '/api-explorer', icon: Code2 },
  { labelKey: 'mcpTools', href: '/settings/mcp', icon: Wrench },
  { labelKey: 'logs', href: '/logs', icon: ScrollText },
  { labelKey: 'settings', href: '/settings/general', icon: Settings },
];

const ACTION_ITEMS = [
  { labelKey: 'createEntity', href: '/settings/entities/new', icon: Plus },
  { labelKey: 'deploySite', href: '/sites', icon: Globe },
  { labelKey: 'generateApiKey', href: '/settings/general', icon: Key },
  { labelKey: 'openApiDocs', href: '/api-explorer', icon: FileText },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [entities, setEntities] = useState<EntityDefinition[]>([]);
  const navigate = useNavigate();
  const { isAuthenticated, hasTenant } = useAuth();
  const t = useTranslations('commandPalette');
  const tNav = useTranslations('nav');

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Load entities when palette opens
  useEffect(() => {
    if (open && isAuthenticated && hasTenant) {
      api.metadata
        .getEntities()
        .then(setEntities)
        .catch(console.error);
    }
  }, [open, isAuthenticated, hasTenant]);

  const goTo = useCallback(
    (href: string) => {
      setOpen(false);
      navigate(href);
    },
    [navigate]
  );

  if (!isAuthenticated) return null;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={t('placeholder')} />
      <CommandList>
        <CommandEmpty>{t('noResults')}</CommandEmpty>

        {entities.length > 0 && (
          <CommandGroup heading={t('entities')}>
            {entities.map((entity) => (
              <CommandItem
                key={entity.id}
                value={`entity ${entity.displayName || entity.name}`}
                onSelect={() => goTo(`/entities/${entity.name}`)}
              >
                <Database className="mr-2 h-4 w-4 text-muted-foreground" />
                <span>{entity.displayName || entity.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {`${entity.fields?.length || 0} ${t('fields')}`}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading={t('actions')}>
          {ACTION_ITEMS.map((item) => (
            <CommandItem
              key={item.href}
              value={`action ${t(item.labelKey)}`}
              onSelect={() => goTo(item.href)}
            >
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{t(item.labelKey)}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('navigation')}>
          {NAVIGATION_ITEMS.map((item) => (
            <CommandItem
              key={item.href}
              value={`nav ${tNav(item.labelKey)}`}
              onSelect={() => goTo(item.href)}
            >
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{tNav(item.labelKey)}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
